import { Skeleton } from "@/components/ui/skeleton";
import { PAGE_WIDTHS, type PageWidth } from "@/lib/page-width";
import { cn } from "@/lib/utils";

/**
 * A page still waiting for its main query: the header and a few blocks where
 * the content will land, so nothing jumps when it arrives.
 *
 * The rule it carries is *a skeleton for a page or a card, a spinner inside a
 * button* — it replaces the "Loading..." page titles, the hand-rolled
 * `border-b-2` spinners and the grey "Chargement…" lines.
 *
 * It draws its own header rather than going through `PageLayout`, whose title
 * is a string and ends up in the tab: a page that does not know its name yet
 * has nothing to put there. The frame mirrors `PageLayout`'s.
 */
export function PageSkeleton({
  width = "default",
  blocks = ["h-48", "h-64"],
  header = true,
}: {
  width?: PageWidth;
  /** One height class per block, top to bottom. */
  blocks?: string[];
  /** Off when the page's own header is already on screen. */
  header?: boolean;
}) {
  return (
    <div
      aria-busy
      className={cn(
        "space-y-6",
        header && "mx-auto pt-8 pb-16",
        header && PAGE_WIDTHS[width],
      )}
    >
      {header && (
        <div className="space-y-2">
          <Skeleton className="h-8 w-56" />
          <Skeleton className="h-4 w-80 max-w-full" />
        </div>
      )}
      {blocks.map((height, i) => (
        <Skeleton key={i} className={cn("w-full rounded-lg", height)} />
      ))}
    </div>
  );
}
